import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="container" style={{ maxWidth: '800px', margin: '20px auto', padding: '20px' }}>
      <h1>Privacy Policy</h1>
      <p><strong>Last Updated: June 2, 2026</strong></p>

      <p>
        This Privacy Policy describes how ASMR.guru (&quot;we,&quot; &quot;us,&quot; or
        &quot;our&quot;) collects, uses, and protects information when you use our video discovery
        platform at <a href="https://www.asmr.guru">asmr.guru</a>. Questions or concerns? Email us
        at <a href="mailto:privacy@asmr.guru">privacy @asmr.guru</a>.
      </p>

      <nav aria-label="Table of contents">
        <h2>Contents</h2>
        <ol>
          <li><a href="#youtube-api">YouTube API Services and Data</a></li>
          <li><a href="#what-we-collect">What Information We Collect</a></li>
          <li><a href="#how-we-use">How We Use Your Information</a></li>
          <li><a href="#legal-bases">Legal Bases for Processing (GDPR)</a></li>
          <li><a href="#sharing">When and With Whom We Share Information</a></li>
          <li><a href="#cookies">Cookies and Tracking Technologies</a></li>
          <li><a href="#social-login">Google Sign-In</a></li>
          <li><a href="#retention">How Long We Keep Your Information</a></li>
          <li><a href="#security">How We Protect Your Information</a></li>
          <li><a href="#minors">Children&apos;s Privacy</a></li>
          <li><a href="#your-rights">Your Privacy Rights</a></li>
          <li><a href="#dnt">Do Not Track</a></li>
          <li><a href="#us-rights">United States Residents</a></li>
          <li><a href="#international">Other Regions</a></li>
          <li><a href="#updates">Updates to This Policy</a></li>
          <li><a href="#contact">Contact Us</a></li>
        </ol>
      </nav>

      <h2 id="youtube-api">1. YouTube API Services and Data</h2>
      <p>
        ASMR.guru is built using the <strong>YouTube Data API v3</strong>, operated by Google LLC.
        Our use of YouTube API Services is limited to reading publicly available video and channel
        metadata — specifically, video titles, descriptions, thumbnails, duration, view counts,
        publish dates, embeddability status, and channel names. We do not access, request, or store
        any YouTube user account data.
      </p>
      <p>
        All API calls are made server-side using an API key. We do not request or use any OAuth
        scopes that would grant access to your personal YouTube account, subscriptions, watch
        history, playlists, or any other private YouTube data.
      </p>
      <p>
        Video metadata retrieved from the YouTube API is cached in our database solely for
        performance and to minimize redundant API calls. This cache is refreshed on a scheduled
        daily basis. Our use of YouTube API data complies with the{' '}
        <a
          href="https://developers.google.com/terms/api-services-user-data-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements.
      </p>
      <p>
        By using ASMR.guru, you acknowledge that YouTube API Services are also governed by{' '}
        <a
          href="https://www.youtube.com/t/terms"
          target="_blank"
          rel="noopener noreferrer"
        >
          YouTube&apos;s Terms of Service
        </a>{' '}
        and{' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google&apos;s Privacy Policy
        </a>
        .
      </p>

      <h2 id="what-we-collect">2. What Information We Collect</h2>

      <h3>Information You Provide</h3>
      <p>
        <strong>Currently:</strong> The only personal information you can voluntarily provide is
        through our contact form (name, email, and message), which is submitted via Web3Forms and
        retained only long enough to respond to your inquiry.
      </p>
      <p>
        <strong>When accounts launch:</strong> Registration will require only a username, a
        hashed password, and an email address for account verification. You may also save filter
        and preference settings. No other personal data will be solicited.
      </p>

      <h3>Information Collected Automatically</h3>
      <p>
        When you visit ASMR.guru, we and our third-party service providers may automatically
        collect technical information such as your IP address, browser type, operating system,
        referring URLs, and pages visited. This information is used for security, analytics, and
        improving the Service. It is only collected for analytics purposes if you have given
        cookie consent.
      </p>

      <h2 id="how-we-use">3. How We Use Your Information</h2>
      <ul>
        <li>To operate and improve the ASMR.guru platform</li>
        <li>To respond to contact form submissions and support requests</li>
        <li>To authenticate your account (when accounts are available)</li>
        <li>To save your filter preferences (when accounts are available)</li>
        <li>To measure aggregate site traffic and usage patterns (Google Analytics, with consent)</li>
        <li>To serve relevant advertisements (Google AdSense, with consent)</li>
        <li>To comply with applicable legal obligations</li>
      </ul>
      <p>
        We do not sell your personal information. We do not use YouTube API data for any purpose
        other than displaying video and channel information on ASMR.guru.
      </p>

      <h2 id="legal-bases">4. Legal Bases for Processing (GDPR)</h2>
      <p>
        If you are located in the European Economic Area (EEA), United Kingdom, or Switzerland,
        we rely on the following legal bases to process your personal information:
      </p>
      <ul>
        <li>
          <strong>Consent:</strong> For analytics and advertising cookies (you may withdraw consent
          at any time via the cookie banner or by clearing your browser cookies).
        </li>
        <li>
          <strong>Legitimate Interests:</strong> To operate and secure the Service, prevent fraud,
          and improve our platform.
        </li>
        <li>
          <strong>Contract:</strong> To provide account services you have requested (when accounts
          are available).
        </li>
        <li>
          <strong>Legal Obligations:</strong> To comply with applicable laws and regulations.
        </li>
      </ul>
      <p>
        If you are located in Canada, we process your information only with your express or implied
        consent, or as otherwise permitted under applicable Canadian privacy law.
      </p>

      <h2 id="sharing">5. When and With Whom We Share Information</h2>
      <p>We do not sell or rent personal information. We may share information in these limited circumstances:</p>
      <ul>
        <li>
          <strong>Google LLC:</strong> We use Google Analytics, Google AdSense, and the YouTube
          Data API. Information shared with Google is governed by{' '}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google&apos;s Privacy Policy
          </a>
          .
        </li>
        <li>
          <strong>Web3Forms:</strong> Contact form submissions are transmitted through Web3Forms
          to deliver your message to us.
        </li>
        <li>
          <strong>Business Transfers:</strong> If ASMR.guru is involved in a merger, acquisition,
          or sale of assets, user data may be transferred as part of that transaction.
        </li>
        <li>
          <strong>Legal Compliance:</strong> We may disclose information if required by law,
          court order, or to protect the rights and safety of our users or the public.
        </li>
      </ul>

      <h2 id="cookies">6. Cookies and Tracking Technologies</h2>
      <p>
        ASMR.guru implements <strong>Google Consent Mode v2</strong>. All tracking and advertising
        cookies default to &quot;denied&quot; until you explicitly accept via the consent banner.
        The types of cookies we use:
      </p>
      <ul>
        <li>
          <strong>Essential / Security Cookies:</strong> Required for the Service to function
          (e.g., session authentication cookie when accounts are available). Always active.
        </li>
        <li>
          <strong>Analytics Cookies (Google Analytics / GA4):</strong> Used to measure aggregate
          site traffic, page views, and user behavior. Active only with your consent.
          <br />
          Opt out: <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a>
        </li>
        <li>
          <strong>Advertising Cookies (Google AdSense):</strong> Used to serve relevant
          advertisements based on demographic and interest data. Active only with your consent.
          <br />
          Opt out:{' '}
          <a href="https://adssettings.google.com/" target="_blank" rel="noopener noreferrer">Google Ad Settings</a>
          {' '}or{' '}
          <a href="http://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer">NAI Opt-Out</a>
        </li>
      </ul>
      <p>
        You may withdraw consent at any time by clearing your browser cookies. Most browsers also
        allow you to block or delete cookies through their settings.
      </p>

      <h2 id="social-login">7. Google Sign-In</h2>
      <p>
        When Google Sign-In becomes available, ASMR.guru will request only your Google account
        email address. This email is used solely to identify and authenticate your account. We will
        not access your Google profile, contacts, YouTube account, subscriptions, watch history, or
        any other Google services data.
      </p>
      <p>
        If you sign in with Google, your authentication session is governed in part by{' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google&apos;s Privacy Policy
        </a>
        .
      </p>

      <h2 id="retention">8. How Long We Keep Your Information</h2>
      <ul>
        <li>
          <strong>Account data</strong> (username, email, preferences): Retained for the lifetime
          of your account. Deleted within 30 days of an account deletion request.
        </li>
        <li>
          <strong>Contact form submissions:</strong> Retained only long enough to respond to your
          inquiry, then deleted.
        </li>
        <li>
          <strong>Analytics data:</strong> Governed by Google Analytics retention settings
          (configurable by us; currently set to 14 months).
        </li>
        <li>
          <strong>YouTube API cache:</strong> Video and channel metadata is refreshed daily.
          No personal information is stored in this cache.
        </li>
      </ul>

      <h2 id="security">9. How We Protect Your Information</h2>
      <p>
        We implement appropriate technical and organizational security measures, including encrypted
        data transmission (HTTPS), hashed password storage, and access controls. However, no
        electronic system is completely secure, and we cannot guarantee absolute security of data
        transmitted over the internet.
      </p>

      <h2 id="minors">10. Children&apos;s Privacy</h2>
      <p>
        ASMR.guru is not directed at children under 18 years of age. We do not knowingly collect
        personal information from minors. If you believe we have inadvertently collected information
        from a minor, please contact us at{' '}
        <a href="mailto:privacy@asmr.guru">privacy @asmr.guru</a> and we will delete it promptly.
      </p>

      <h2 id="your-rights">11. Your Privacy Rights</h2>
      <p>
        Depending on your location, you may have the following rights regarding your personal
        information:
      </p>
      <ul>
        <li>Right to access and obtain a copy of your personal data</li>
        <li>Right to correct inaccurate data</li>
        <li>Right to deletion (&quot;right to be forgotten&quot;)</li>
        <li>Right to restrict or object to processing</li>
        <li>Right to data portability</li>
        <li>Right to withdraw consent at any time (without affecting prior lawful processing)</li>
        <li>Right to lodge a complaint with your local data protection authority</li>
      </ul>
      <p>
        To exercise any of these rights, email{' '}
        <a href="mailto:privacy@asmr.guru">privacy @asmr.guru</a>. We will respond within the
        timeframe required by applicable law (typically 30–45 days).
      </p>
      <p>
        EEA/UK residents may also lodge complaints with their Member State data protection authority
        or the UK Information Commissioner&apos;s Office (ICO). Swiss residents may contact the
        Federal Data Protection and Information Commissioner (FDPIC).
      </p>

      <h2 id="dnt">12. Do Not Track</h2>
      <p>
        Some browsers transmit a Do Not Track (DNT) signal. Because there is no uniform standard
        for interpreting DNT signals, ASMR.guru does not currently alter its data practices in
        response to DNT signals. California law requires us to disclose this. Our cookie consent
        banner provides a meaningful opt-out for tracking technologies.
      </p>

      <h2 id="us-rights">13. United States Residents</h2>
      <p>
        Residents of California, Colorado, Connecticut, Virginia, Texas, and other US states with
        comprehensive privacy laws have additional rights, including:
      </p>
      <ul>
        <li>Right to know whether we process your personal data and to access it</li>
        <li>Right to correct inaccuracies</li>
        <li>Right to delete your personal data</li>
        <li>Right to opt out of targeted advertising or sale of personal data</li>
        <li>Right to non-discrimination for exercising your privacy rights</li>
      </ul>
      <p>
        <strong>We do not sell personal information.</strong> We honor the Global Privacy Control
        (GPC) opt-out signal.
      </p>
      <p>
        To exercise your US state privacy rights, email{' '}
        <a href="mailto:privacy@asmr.guru">privacy @asmr.guru</a>.
      </p>
      <p>
        <em>California &quot;Shine the Light&quot;:</em> California residents may request
        information about personal information disclosed to third parties for direct marketing
        purposes. We do not disclose personal information to third parties for their direct
        marketing purposes.
      </p>

      <h2 id="international">14. Other Regions</h2>
      <p>
        <strong>Australia and New Zealand:</strong> We process personal information in accordance
        with Australia&apos;s Privacy Act 1988 and New Zealand&apos;s Privacy Act 2020. You may
        contact the Office of the Australian Information Commissioner or the Office of the New
        Zealand Privacy Commissioner to raise a complaint.
      </p>
      <p>
        <strong>South Africa:</strong> You may request access to or correction of your personal
        information. Complaints may be directed to the Information Regulator (South Africa) at{' '}
        <a href="mailto:enquiries@inforegulator.org.za">enquiries@inforegulator.org.za</a>.
      </p>

      <h2 id="updates">15. Updates to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be indicated by
        an updated &quot;Last Updated&quot; date. We encourage you to review this page periodically.
        Continued use of the Service after changes are posted constitutes acceptance of the revised
        policy.
      </p>

      <h2 id="contact">16. Contact Us</h2>
      <p>
        For privacy-related questions, requests, or complaints, please contact us:
      </p>
      <address style={{ fontStyle: 'normal', lineHeight: '1.8' }}>
        <strong>Email:</strong>{' '}
        <a href="mailto:privacy@asmr.guru">privacy @asmr.guru</a>
        <br />
        <strong>Mail:</strong><br />
        ASMR.guru<br />
        30 N Gould St Ste R<br />
        Sheridan, WY 82801<br />
        United States
      </address>
      <p>
        See also our <Link to="/tos">Terms of Service</Link>.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
